
import { Order } from "../../types/Sorters";
import { useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { ArrowLeft } from "lucide-react";
import { logger } from "../helpers/logHandler";
import { playSuccess, playError } from "../helpers/audioPlayer";
import { useTranslation } from "../../i18n";
import HiddenScanInput from "../HiddenScanInput";
import ScanStatusIcons from "../ScanStatusIcons";
import ScanFlashOverlay from "../ScanFlashOverlay";
import FitWord from "../FitWord";
import { apiFetch } from "../../api/apiFetch";
import { zoneDispatchReasonKey } from "./zoneDispatchReasons";
import "../../styles/scansheet.css";

const SorterTwoSingleOrderDisplay = ({ order, closeOrder }: { order: Order, closeOrder: (completedOrder: boolean, tag: string) => void }) => {

    const { t } = useTranslation();
    const [tagInUse, set_tagInUse] = useState<string>("");
    const [inputString, set_inputString] = useState<string>("");
    const [lastScanned, set_lastScanned] = useState<string>("");
    const [scannedLocations, set_scannedLocations] = useState<string[]>([]);
    const [orderLocations, set_orderLocations] = useState<string[]>(order.Locations);
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    // The opening tag scan asks the BE whether the tote is free and the
    // Portal order is ready (zone_dispatch_via_portal). One check in
    // flight at a time so a double-trigger can't open two tags.
    const checkingTagRef = useRef(false);

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 500);
    };

    const handleTagScan = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? inputString;
        const scannedTag = val;
        set_inputString("");

        if (scannedTag.slice(0, 3) === "NPS" || scannedTag.slice(0, 3) === "PLS" || scannedTag.length > 8 || scannedTag === "") {
            const logState = lastScanned === scannedTag ? "repeat" : "error";
            logger(order.Warehouse, "S2 Unloader", order.OrderNumber, 0, scannedTag, "Open Tag", "N/A", "N/A", logState);
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("invalid_tag"));
            return;
        }

        if (tagInUse === "") {
            if (checkingTagRef.current) return;
            checkingTagRef.current = true;
            // Pre-check with the BE: is the tote free, is the Portal order
            // ready? With the warehouse's Portal-dispatch switch off the BE
            // answers ok (nothing to check), and a network failure is not a
            // refusal — the closing scan re-checks everything under locks.
            let refusal: { reason?: string; status?: string } | null = null;
            try {
                const res = await apiFetch(`/zonestorage/orders/check-tag`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ warehouse: order.Warehouse, tag: scannedTag, orderNumber: order.OrderNumber }),
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data && data.ok === false) refusal = data;
                }
            } catch {
                refusal = null;
            } finally {
                checkingTagRef.current = false;
            }
            if (refusal) {
                logger(order.Warehouse, "S2 Unloader", order.OrderNumber, 0, scannedTag, "Open Tag", "N/A", refusal.reason ?? "refused", "error");
                flashScanStatus("error");
                playError();
                toast.dismiss();
                toast.error(t(zoneDispatchReasonKey(refusal.reason), { status: refusal.status ?? "", order: order.OrderNumber }));
                return;
            }
            set_tagInUse(scannedTag);
            logger(order.Warehouse, "S2 Unloader", order.OrderNumber, 0, scannedTag, "Open Tag", "N/A", "N/A", "success");
            flashScanStatus("success");
            playSuccess();
            return
        }

        // Compare opening and closing tag
        if (tagInUse !== scannedTag) {
            console.log("Opening and closing tags don't match");
            logger(order.Warehouse, "S2 Unloader", order.OrderNumber, 0, scannedTag, "Close Tag", "N/A", "N/A", "error");
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("tags_dont_match"));
            return;

        } else {
            // Close order
            closeOrder(true, tagInUse);
            logger(order.Warehouse, "S2 Unloader", order.OrderNumber, 0, scannedTag, "Close Tag", "N/A", "N/A", "success");
        }

    }, [inputString, tagInUse, lastScanned, order, closeOrder, t]);

    const handleLocationScan = useCallback((scannedValue?: string) => {
        const val = scannedValue ?? inputString;
        // Reset the inputString and save what was input
        const scannedLocation = val;
        set_inputString("");

        // Check if scanned location is a correct location
        const locationInArray = orderLocations.indexOf(scannedLocation);

        if (locationInArray !== -1) {
            set_scannedLocations([...scannedLocations, scannedLocation].sort());
            set_orderLocations(orderLocations.filter((locations) => locations !== scannedLocation));
            logger(order.Warehouse, "S2 Unloader", order.OrderNumber, 0, scannedLocation, "Bay Location", scannedLocation, tagInUse, "success");
        } else {
            playError();
            toast.dismiss(); toast.error(t("location_not_in_order"));
            console.log("Location not valid");
            logger(order.Warehouse, "S2 Unloader", order.OrderNumber, 0, scannedLocation, "Bay Location", scannedLocation, tagInUse, "wrong location");
        }
    }, [inputString, orderLocations, scannedLocations, tagInUse, order, t]);

    const handleSubmit = useCallback((scannedValue?: string) => {
        if (tagInUse === "" || scannedLocations.length === order.Locations.length) {
            handleTagScan(scannedValue);
        } else {
            handleLocationScan(scannedValue);
        }
    }, [tagInUse, scannedLocations.length, order.Locations.length, handleTagScan, handleLocationScan]);

    return (
        <div className="scan-panel">
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("order_title", { number: order.OrderNumber })}</h1>
                <button className="page-back-btn" onClick={() => closeOrder(false, "")}><ArrowLeft size={20} /></button>
            </div>

            <div className="scan-centre">
                {/* Green success tick removed — keep the red X (error). */}
                <ScanStatusIcons status={scanStatus === "success" || scanStatus === "error" ? "idle" : scanStatus} />

                <div className="scan-feedback-area">
                    <h3 className="no-margin">{t("bay_label", { bay: order.Bay })}</h3>
                    <h3 className="no-margin">{t("items")}: {order.NumberOfItems.toString()}</h3>
                    <h3 className="no-margin">{t("tag_label", { tag: tagInUse === "" ? "Not Scanned" : tagInUse })}</h3>
                    {order.Priority && <h3 className="no-margin align-center">{t("priority")}</h3>}
                </div>

                {(tagInUse === "" || (scannedLocations.length === order.Locations.length)) && (
                    <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                        {t("scan_tag_suffix", { suffix: tagInUse !== "" ? "To Close Order" : "" }).toUpperCase().split(" ").map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                )}

                {(tagInUse !== "" && (scannedLocations.length !== order.Locations.length)) && (
                    <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                        {t("scan_all_locations").toUpperCase().split(" ").map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                )}

                <div className="flex order-scan-locations-container">
                    <div>
                        <h2>{t("to_scan")}</h2>
                        {orderLocations.map((location) => <h3 key={location}>{location}</h3>)}
                    </div>

                    <div>
                        <h2>{t("scanned")}</h2>
                        {scannedLocations.map((location) => <h3 key={location}>{location}</h3>)}
                    </div>
                </div>
            </div>
            <HiddenScanInput value={inputString} onChange={set_inputString} onSubmit={handleSubmit} disabled={false} disableCamera />
        </div>
    );
}

export default SorterTwoSingleOrderDisplay;
